import { defineStore } from 'pinia';
import { collection, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { generateSlug, makeSlugUnique } from '~/lib/slugify';
import { stripUndefined } from '~/lib/stripUndefined';
import { resolveWorkspaceEntitlements } from '~/lib/subscriptionPlans';
import { completePassedProjectMilestones, resolveProjectMilestones } from '~/lib/projectMilestones';
import { isProjectCompleted } from '~/lib/projectLifecycle';
import type { Client, Project } from '~/types/models';
import { runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';

function summarizeActivityValue(value: unknown): string {
    if (value === undefined || value === null || value === '') {
        return 'unset';
    }

    if (typeof value === 'object') {
        if ('status' in (value as Record<string, unknown>) && 'version' in (value as Record<string, unknown>)) {
            const contract = value as Record<string, unknown>;
            const status = String(contract.status || 'awaiting_client');
            const version = Number(contract.version || 1);
            return `contract v${version} (${status})`;
        }

        try {
            return JSON.stringify(value);
        } catch {
            return '[complex value]';
        }
    }

    return String(value);
}

export const useProjectStore = defineStore('projects', {
    state: () => ({
        projects: [] as Project[],
        currentProject: null as Project | null,
        dataTenantId: null as string | null,
        loadedForTenantId: null as string | null,
        loadedClientIds: [] as string[],
    }),

    actions: {
        _resolveClientName(clientId?: string, clientName?: string): string | undefined {
            if (clientName?.trim()) {
                return clientName;
            }

            if (!clientId) {
                return undefined;
            }

            const clientStore = useClientStore();
            const matchingClient = clientStore.clients.find((client: Client) => client.id === clientId);
            return matchingClient?.name;
        },

        async _ensureClientsLoaded(): Promise<void> {
            const clientStore = useClientStore();
            await clientStore.fetchClients();
        },

        async _resolveCanonicalClientId(input: {
            clientId?: string;
        }): Promise<string | undefined> {
            const explicitClientId = String(input.clientId || '').trim();
            if (explicitClientId) {
                return explicitClientId;
            }
            return undefined;
        },

        _hydrateProject(project: Project): Project {
            const resolvedClientName = this._resolveClientName(project.clientId, project.clientName);

            return {
                ...project,
                clientName: resolvedClientName,
                client: resolvedClientName || project.client || project.clientId,
                stages: resolveProjectMilestones(project.stages),
            };
        },

        async _syncClientNameIfMissing(project: Project): Promise<void> {
            if (!project.id || project.clientName || !project.clientId) {
                return;
            }

            const resolvedClientName = this._resolveClientName(project.clientId);
            if (!resolvedClientName) {
                return;
            }

            const { $db } = useNuxtApp();

            try {
                const tenantId = this._getTenantId();
                const projectRef = doc($db, `tenants/${tenantId}/projects`, project.id);
                await updateDoc(projectRef, {
                    clientName: resolvedClientName,
                    client: resolvedClientName,
                    updatedAt: new Date(),
                });
            } catch (error) {
                console.warn('Failed to backfill project client name:', error);
            }
        },

        /**
         * Gets the current tenant ID from the tenant store
         * @private
         */
        _getTenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantStore.currentTenantId;
        },

        /**
         * Fetches all projects for the current tenant from Firestore.
         * 
         * Retrieves documents from the 'tenants/{currentTenantId}/projects' collection
         * and maps them to project objects with their document IDs.
         * 
         * @async
         * @returns {Promise<void>}
         * @throws Logs errors to console if the Firestore query fails
         * 
         * @example
         * await store.fetchProjects();
         */
        async fetchProjects(options: FreshRequestOptions = {}): Promise<void> {
            const { $db } = useNuxtApp();
            try {
                const tenantId = this._getTenantId();
                if (this.dataTenantId && this.dataTenantId !== tenantId) {
                    this.projects = [];
                    this.currentProject = null;
                    this.loadedForTenantId = null;
                    this.loadedClientIds = [];
                }
                await runFreshRequest(`projects:${tenantId}:all`, async () => {
                    await this._ensureClientsLoaded();
                    const collectionRef = collection($db, `tenants/${tenantId}/projects`);
                    const snapshot = await getDocs(collectionRef);
                    const projects = snapshot.docs.map(projectDoc => this._hydrateProject({
                        id: projectDoc.id, ...projectDoc.data()
                    } as Project));
                    this.projects = projects;
                    this.loadedForTenantId = tenantId;
                    this.dataTenantId = tenantId;
                    this.loadedClientIds = [];
                    await Promise.all(projects.map(project => this._syncClientNameIfMissing(project)));
                }, {
                    ...options,
                    hasData: this.loadedForTenantId === tenantId,
                });
            } catch (error) {
                console.error("Error fetching projects: ", error);
            }
        },

        async fetchProjectById(projectId: string): Promise<Project | null> {
            const tenantId = this._getTenantId();
            if (this.dataTenantId && this.dataTenantId !== tenantId) {
                this.projects = [];
                this.currentProject = null;
                this.loadedForTenantId = null;
                this.loadedClientIds = [];
            }
            const cached = this.dataTenantId === tenantId
                ? this.projects.find(project => project.id === projectId)
                : null;
            if (cached) {
                this.currentProject = cached;
                return cached;
            }

            const { $db } = useNuxtApp();
            try {
                await this._ensureClientsLoaded();
                const docRef = doc($db, `tenants/${tenantId}/projects`, projectId);
                const snap = await getDoc(docRef);
                if (!snap.exists()) {
                    this.currentProject = null;
                    return null;
                }
                const project = this._hydrateProject({ id: snap.id, ...snap.data() } as Project);
                this.currentProject = project;
                this.dataTenantId = tenantId;
                this.projects = [...this.projects.filter(item => item.id !== project.id), project];
                await this._syncClientNameIfMissing(project);
                return project;
            } catch (error) {
                console.error('Error fetching project by ID:', error);
                this.currentProject = null;
                return null;
            }
        },

        /**
         * Fetch a single project by its slug field.
         * Sets `currentProject` when found; returns null if not.
         */
        async fetchProjectBySlug(slug: string): Promise<Project | null> {
            const { $db } = useNuxtApp();
            try {
                await this._ensureClientsLoaded();
                const tenantId = this._getTenantId();
                const q = query(
                    collection($db, `tenants/${tenantId}/projects`),
                    where('slug', '==', slug)
                );
                const snapshot = await getDocs(q);
                if (snapshot.empty) {
                    this.currentProject = null;
                    return null;
                }
                const docSnap = snapshot.docs.at(0);
                if (!docSnap) {
                    this.currentProject = null;
                    return null;
                }
                this.currentProject = this._hydrateProject({ id: docSnap.id, ...docSnap.data() } as Project);
                await this._syncClientNameIfMissing(this.currentProject);
                return this.currentProject;
            } catch (error) {
                console.error('Error fetching project by slug:', error);
                this.currentProject = null;
                return null;
            }
        },

        /**
         * Fetches projects associated with a specific client ID.
         * 
         * Attempts to retrieve projects from the local cache first. If no cached projects
         * are found, queries the Firestore database for projects matching the given client ID.
         * 
         * @param clientId - The unique identifier of the client
         * @returns A promise that resolves to an array of projects for the client, or null if no projects are found or an error occurs
         * @throws Does not throw; errors are caught and logged to console
         * 
         * @example
         * const projects = await store.fetchProjectsByClientId('client-123');
         */
        async fetchProjectsByClientId(clientId: string, options: FreshRequestOptions = {}): Promise<Project[] | null> {
            await this._ensureClientsLoaded();
            const cached = this.projects.filter(project => project.clientId === clientId);
            const tenantId = this._getTenantId();
            if (this.dataTenantId && this.dataTenantId !== tenantId) {
                this.projects = [];
                this.currentProject = null;
                this.loadedForTenantId = null;
                this.loadedClientIds = [];
            }
            if (!options.force && (this.loadedForTenantId === tenantId || this.loadedClientIds.includes(clientId))) {
                const hydratedCached = cached.map(project => this._hydrateProject(project));
                return hydratedCached.length ? hydratedCached : null;
            }

            const { $db } = useNuxtApp();
            try {
                await runFreshRequest(`projects:${tenantId}:client:${clientId}`, async () => {
                    const clientQuery = query(
                        collection($db, `tenants/${tenantId}/projects`),
                        where('clientId', '==', clientId)
                    );
                    const snapshot = await getDocs(clientQuery);
                    const projects = snapshot.docs.map(entry => this._hydrateProject({
                        id: entry.id,
                        ...entry.data(),
                    } as Project));
                    const projectIds = new Set(projects.map(project => project.id));
                    this.projects = [
                        ...this.projects.filter(project => project.clientId !== clientId && !projectIds.has(project.id)),
                        ...projects,
                    ];
                    if (!this.loadedClientIds.includes(clientId)) {
                        this.loadedClientIds = [...this.loadedClientIds, clientId];
                    }
                    this.dataTenantId = tenantId;
                    await Promise.all(projects.map(project => this._syncClientNameIfMissing(project)));
                }, {
                    ...options,
                    hasData: this.loadedClientIds.includes(clientId),
                });

                const projects = this.projects.filter(project => project.clientId === clientId);
                return projects.length ? projects : null;
            } catch (error) {
                console.error("Error fetching projects by client ID: ", error);
                return null;
            }
        },

        /**
         * Adds a new project to the current tenant's project collection.
         * Auto-generates a unique slug from the project name.
         * @param projectData - The project data to be added
         * @param userId - The ID of the user creating the project
         * @param clientId - Canonical client ID fallback
         * @returns {Promise<string>} The ID of the created project
         * @throws Logs error to console if the addition fails
         */
        async addProject(projectData: Omit<Project, 'id' | 'slug' | 'createdAt' | 'updatedAt'>, userId: string, clientId?: string): Promise<string> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const collectionRef = collection($db, `tenants/${tenantId}/projects`);
            await this._ensureClientsLoaded();
            const tenantStore = useTenantStore();
            const entitlements = resolveWorkspaceEntitlements(tenantStore.currentTenant);
            if (typeof entitlements.plan.features.projectLimit === 'number') {
                const currentProjects = this.projects.length > 0
                    ? this.projects
                    : (await getDocs(collectionRef)).docs.map((projectDoc) => ({ id: projectDoc.id, ...projectDoc.data() } as Project));
                const activeProjectsCount = currentProjects.filter((project) => !['completed', 'archived'].includes(String(project.status || '').toLowerCase())).length;
                if (activeProjectsCount >= entitlements.plan.features.projectLimit) {
                    throw new Error(`Your ${entitlements.plan.name} plan supports up to ${entitlements.plan.features.projectLimit} active projects. Upgrade to create another project.`);
                }
            }
            const resolvedClientId = await this._resolveCanonicalClientId({
                clientId: clientId || projectData.clientId,
            });
            if (!resolvedClientId) {
                throw new Error('Projects must belong to a client. Choose or create the client before saving.');
            }
            const resolvedClientName = this._resolveClientName(resolvedClientId, projectData.clientName);

            // Firestore rejects undefined values, so remove any optional fields
            // the caller left unset (e.g., status, dueDate, health, stage).
            const sanitizedProjectData = stripUndefined(projectData) as Omit<Project, 'id' | 'slug' | 'createdAt' | 'updatedAt'>;

            // Generate unique slug from project name
            const baseSlug = generateSlug(sanitizedProjectData.name || 'untitled-project');
            const uniqueSlug = makeSlugUnique(baseSlug);

            const project = {
                ...sanitizedProjectData,
                profileImageURL: sanitizedProjectData.profileImageURL || '',
                stages: resolveProjectMilestones(sanitizedProjectData.stages),
                stage: sanitizedProjectData.stage || 'Design',
                slug: uniqueSlug,
                client: resolvedClientName || resolvedClientId,
                ...(resolvedClientId ? { clientId: resolvedClientId } : {}),
                ...(resolvedClientName ? { clientName: resolvedClientName } : {}),
                portalShared: sanitizedProjectData.portalShared === true,
                createdBy: userId,
                lastUpdatedBy: userId,
                createdAt: new Date(),
                updatedAt: new Date()
            }

            try {
                const docRef = await addDoc(collectionRef, project);
                this.projects.push({ id: docRef.id, ...project } as Project);

                // Log activity
                const userStore = useUserStore();
                const activityStore = useActivityStore();
                try {
                    await activityStore.logActivity({
                        action: 'create',
                        entityType: 'project',
                        entityId: docRef.id,
                        description: `Created project: ${project.name}`,
                        userId,
                        userName: userStore.user?.displayName,
                        userAvatar: userStore.user?.avatarURL,
                        changedField: undefined
                    });
                } catch (err) {
                    // Log but don't fail - activity logging is not critical
                    console.warn('Failed to log project creation activity:', err);
                }

                return docRef.id;
            } catch (error) {
                console.error("Error adding project: ", error);
                throw error;
            }
        },

        async updateProject(projectId: string, updatedData: Partial<Project>): Promise<void> {
            const { $db } = useNuxtApp();
            try {
                await this._ensureClientsLoaded();
                const tenantStore = useTenantStore();
                const tenantId = this._getTenantId();
                const docRef = doc($db, `tenants/${tenantId}/projects`, projectId);
                const currentProject = this.projects.find(project => project.id === projectId) || this.currentProject;
                const resolvedClientId = await this._resolveCanonicalClientId({
                    clientId: updatedData.clientId ?? currentProject?.clientId,
                });
                if (!resolvedClientId) {
                    throw new Error('Projects must belong to a client. Choose or create the client before saving.');
                }
                const resolvedClientName = this._resolveClientName(resolvedClientId, updatedData.clientName ?? currentProject?.clientName);
                const sanitizedData = stripUndefined(updatedData) as Partial<Project>;
                const sourceStages = resolveProjectMilestones(sanitizedData.stages || currentProject?.stages);
                const transitionedStages = sanitizedData.stage
                    ? completePassedProjectMilestones(
                        sourceStages,
                        currentProject?.stage,
                        sanitizedData.stage,
                    )
                    : sourceStages;
                
                // Capture previous state for activity logging
                const previousProject = this.projects.find(p => p.id === projectId);

                const updateData = {
                    ...sanitizedData,
                    ...(transitionedStages.length > 0
                        ? { stages: transitionedStages }
                        : {}),
                    ...(resolvedClientId ? { clientId: resolvedClientId } : {}),
                    ...(resolvedClientName
                        ? { clientName: resolvedClientName, client: resolvedClientName }
                            : {}),
                    ...(transitionedStages.length > 0 && sanitizedData.stages && !sanitizedData.stage
                        ? { stage: transitionedStages[0]?.name || currentProject?.stage || 'Design' }
                        : {}),
                    updatedAt: new Date()
                };
                
                await updateDoc(docRef, updateData);
                
                const index = this.projects.findIndex(p => p.id === projectId);
                if (index !== -1) {
                    this.projects[index] = this._hydrateProject({ ...this.projects[index], ...updateData } as Project);
                }

                if (this.currentProject?.id === projectId) {
                    this.currentProject = this._hydrateProject({ ...this.currentProject, ...updateData } as Project);
                }

                const nextStatus = String(updateData.status || '').trim().toLowerCase();
                const previousStatus = String(previousProject?.status || '').trim().toLowerCase();
                const configuredStatusAutomation = tenantStore.currentTenant?.projectSettings?.statusAutomation;
                const legacyTaskAction = tenantStore.currentTenant?.projectSettings?.defaultProjectCompletedTaskAction;
                const automationTriggerStatus = String(configuredStatusAutomation?.triggerStatus || '').trim().toLowerCase();
                const automationTaskAction = configuredStatusAutomation?.taskAction || legacyTaskAction || 'none';
                const reachedConfiguredCompletion = Boolean(automationTriggerStatus)
                    && nextStatus === automationTriggerStatus
                    && previousStatus !== automationTriggerStatus
                    && automationTaskAction === 'mark_done';
                const shouldCompleteLinkedTasks = (
                    (isProjectCompleted(nextStatus) && !isProjectCompleted(previousStatus))
                    || reachedConfiguredCompletion
                );

                if (shouldCompleteLinkedTasks) {
                    const taskStore = useTaskStore();
                    if (taskStore.tasks.length === 0) {
                        await taskStore.fetchTasks();
                    }
                    await taskStore.markProjectTasksCompleted(projectId);
                }

                // Log activities for changed fields
                const userStore = useUserStore();
                const activityStore = useActivityStore();
                try {
                    const changedFields = Object.keys(sanitizedData) as Array<keyof Partial<Project>>;
                    for (const field of changedFields) {
                        if (field === 'updatedAt') continue;
                        const previousValue = previousProject ? (previousProject[field] ?? 'unset') : 'unset';
                        const newValue = sanitizedData[field] ?? 'unset';
                        if (String(previousValue) !== String(newValue)) {
                            const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
                            const action = field === 'status' || field === 'stage' ? 'status_change' : 'update';
                            const summaryPreviousValue = summarizeActivityValue(previousValue);
                            const summaryNewValue = summarizeActivityValue(newValue);
                            await activityStore.logActivity({
                                action,
                                entityType: 'project',
                                entityId: projectId,
                                description: `Updated ${fieldName}: ${summaryPreviousValue} → ${summaryNewValue}`,
                                userId: userStore.user?.id || 'unknown',
                                userName: userStore.user?.displayName,
                                userAvatar: userStore.user?.avatarURL,
                                changedField: field as string,
                                previousValue: summaryPreviousValue,
                                newValue: summaryNewValue
                            });
                        }
                    }
                } catch (err) {
                    // Log but don't fail - activity logging is not critical
                    console.warn('Failed to log project update activity:', err);
                }
            } catch (error) {
                console.error("Error updating project: ", error);
                throw error;
            }
        },

        /**
         * Deletes a project by its ID.
         * @param projectId - The unique identifier of the project to delete
         * @returns A promise that resolves when the project has been successfully deleted
         * @throws An error if the project deletion fails or if the project ID is invalid
         */
        async deleteProject(projectId: string): Promise<void> {
            const { $db } = useNuxtApp();
            try {
                const tenantId = this._getTenantId();
                const projectToDelete = this.projects.find(p => p.id === projectId);
                const docRef = doc($db, `tenants/${tenantId}/projects`, projectId);
                await deleteDoc(docRef);
                this.projects = this.projects.filter(p => p.id !== projectId);

                try {
                    await useEntityTagsStore().removeEntityTags('project', projectId);
                } catch (tagError) {
                    console.warn('Failed to remove project tags after deletion:', tagError);
                }

                // Log activity
                const userStore = useUserStore();
                const activityStore = useActivityStore();
                try {
                    await activityStore.logActivity({
                        action: 'delete',
                        entityType: 'project',
                        entityId: projectId,
                        description: `Deleted project: ${projectToDelete?.name || 'Unknown'}`,
                        userId: userStore.user?.id || 'unknown',
                        userName: userStore.user?.displayName,
                        userAvatar: userStore.user?.avatarURL,
                        changedField: undefined
                    });
                } catch (err) {
                    // Log but don't fail - activity logging is not critical
                    console.warn('Failed to log project deletion activity:', err);
                }
            } catch (error) {
                console.error("Error deleting project: ", error);
                throw error;
            }
        }
    }
})
